import { useContext, useMemo } from 'react'

import { I18nContext, type I18n } from './i18n-context'
import {
  formatDate,
  formatDateMedium,
  formatDateTime,
  formatList,
  formatNumber,
  formatRelative,
} from './format'
import { DEFAULT_LOCALE } from './locale'
import { translate, type TranslateVars } from './translate'

/**
 * El idioma de quien está mirando, con todo lo que hace falta para escribirle.
 *
 * Sin provider cae al idioma por defecto en vez de tirar: un componente suelto
 * tiene que poder dibujarse. Una pantalla en español es un problema menor; una
 * pantalla que no existe, no.
 */
export function useI18n(): I18n {
  const value = useContext(I18nContext)

  const fallback = useMemo<I18n>(
    () => ({
      locale: DEFAULT_LOCALE,
      t: (key: string, vars?: TranslateVars) => translate(DEFAULT_LOCALE, key, vars),
      formatDate: (v, tz) => formatDate(v, DEFAULT_LOCALE, tz),
      formatDateMedium: (v, tz) => formatDateMedium(v, DEFAULT_LOCALE, tz),
      formatDateTime: (v, tz) => formatDateTime(v, DEFAULT_LOCALE, tz),
      formatNumber: (v, o) => formatNumber(v, DEFAULT_LOCALE, o),
      formatRelative: (v, now) => formatRelative(v, DEFAULT_LOCALE, now),
      formatList: (items) => formatList(items, DEFAULT_LOCALE),
    }),
    [],
  )

  return value ?? fallback
}

/** Atajo para el caso más común: solo texto. */
export function useT(): I18n['t'] {
  return useI18n().t
}
